import { Select, SelectProps } from 'antd';
import React, { useCallback, useMemo, useRef } from 'react';
import { useGetUserProjectsQuery } from '../../apis/apiApplication';
import { ProjectResponse, TenantResponse } from '@ydistri/identity-sdk';

import { RoutingPage } from '../../routes/routerSlice';
import { styled } from 'styled-components';
import { COLORS, computeRemSize } from '@ydistri/theme';
import { SearchableSelectOptionType, selectFilterHandler } from '@ydistri/utils';
import type { BaseSelectRef } from 'rc-select';
import { router } from '../../routes/router';
import { useStableParams } from '../../routes/useStableParams';

interface TTenantWithProjects {
  tenant: TenantResponse;
  projectsForTenant: ProjectResponse[];
}

const OptionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${computeRemSize(22)};
  font-size: ${computeRemSize(14)};
`;

const Divider = styled.div`
  height: ${computeRemSize(1)};
  margin: ${computeRemSize(6)} 0;
`;

const DropdownRenderer = styled.div`
  .rc-virtual-list-holder {
    max-height: 80vh !important;
  }
`;

const dropdownStyle = {
  popup: {
    root: {
      fontSize: `${computeRemSize(32)} !important`,
      minWidth: computeRemSize(300),
    },
  },
};

const dropdownRender: SelectProps['dropdownRender'] = (origin: React.ReactElement) => {
  return <DropdownRenderer>{origin}</DropdownRenderer>;
};

function getTenantsWithProjectsFromProjects(projects: ProjectResponse[]) {
  const tenants: TTenantWithProjects[] = [];

  projects.forEach(project => {
    const relevantTenant = tenants.find(
      tenantWithProject => tenantWithProject.tenant.id === project.tenant.id,
    );
    if (!relevantTenant) {
      const newTenantProjects = [];
      newTenantProjects.push(project);
      tenants.push({
        tenant: project.tenant,
        projectsForTenant: [project],
      });
    } else {
      relevantTenant.projectsForTenant.push(project);
    }
  });

  return tenants;
}

const ProjectSelectStyled = styled(Select<string>)`
  width: 100%;
  padding: 0;

  .ant-select-selector {
    font-size: ${computeRemSize(22)} !important;

    .ant-select-selection-search {
      margin-top: ${computeRemSize(-7)} !important;

      input {
        font-family: Roboto, Helvetica, Arial, sans-serif !important;
        height: ${computeRemSize(22)} !important;
        font-size: ${computeRemSize(22)} !important;
      }
    }

    .ant-select-selection-item {
      overflow: visible;
    }

    .ant-select-selection-item div {
      color: ${COLORS.COLOR_DARK};
      font-size: ${computeRemSize(22)} !important;
      height: ${computeRemSize(22)};
    }
  }

  .ant-select-item {
    font-weight: 500;
  }
`;

export const ProjectSelect: React.FC = () => {
  const ref = useRef<BaseSelectRef>(null);
  const { projectShortName } = useStableParams();
  const { data: projects } = useGetUserProjectsQuery();

  const options: SearchableSelectOptionType[] = useMemo(() => {
    if (projects && projects.length > 0) {
      const tenants = getTenantsWithProjectsFromProjects(projects).sort((a, b) =>
        a.tenant.name.localeCompare(b.tenant.name),
      );

      return tenants.map((t, i) => ({
        searchableString: t.tenant.name,
        label: (
          <>
            {i > 0 && <Divider />}
            {t.tenant.name}
          </>
        ),
        options: t.projectsForTenant.map(p => ({
          value: p.shortName,
          label: <OptionRow>{p.name}</OptionRow>,
          searchableString: p.name,
        })),
      }));
    }

    return [];
  }, [projects]);

  // The project changes only through the URL, so every switch goes the way Back and Forward go
  const onChangeHandler = useCallback((selectedProjectShortName: string) => {
    router.navigate(`/${selectedProjectShortName}/${RoutingPage.CALCULATIONS}`);
  }, []);

  const hasOptions = options.length > 0;
  const value = hasOptions ? projectShortName : undefined;

  const focusOnOpenChange = useCallback((open: boolean) => {
    if (open && ref.current) {
      ref.current.focus();
    }
  }, []);

  return (
    <ProjectSelectStyled
      ref={ref}
      value={value}
      onChange={onChangeHandler}
      onOpenChange={focusOnOpenChange}
      options={options}
      showSearch={true}
      variant="borderless"
      popupMatchSelectWidth={false}
      styles={dropdownStyle}
      popupRender={dropdownRender}
      id="ProjectSelector"
      suffixIcon={null}
      filterOption={selectFilterHandler}
    />
  );
};
